/**
 * LICITARS 3.0 - Chat8 Header Component
 * Navy/Silver themed header with document selection
 * Preserves original functionality while enhancing visual design
 */

import React from "react";
import { MessageSquare, FileText, Loader2 } from "lucide-react";
import { DocumentSelectV3 } from "@/components/DocumentSelectV3";
import { DocumentInfoModal } from "@/components/DocumentInfoModal";

interface Chat8HeaderV3Props {
  documents: any[];
  selectedDocumentId: string | null;
  loadingDocuments: boolean;
  loadingSession: boolean;
  onDocumentSelect: (docId: string) => void;
}

export const Chat8HeaderV3: React.FC<Chat8HeaderV3Props> = ({
  documents,
  selectedDocumentId,
  loadingDocuments,
  loadingSession,
  onDocumentSelect,
}) => {
  const selectedDocument = documents.find(doc => doc.id === selectedDocumentId);

  return (
    <header
      id="chat8-header"
      className="chat8-header bg-navy-700 border-l border-r border-t border-white/5 rounded-t-lg"
      role="banner"
      aria-label="Cabeçalho do Chat"
      data-testid="chat8-header"
    >
      <nav
        id="chat8-nav"
        aria-label="Controles de navegação e seleção de documento"
        className="chat8-nav p-4"
        role="navigation"
        data-testid="chat8-nav"
      >
        {/* Title and Icon */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">
              Chat Inteligente
            </h1>
            <p className="text-sm text-blue-100/70">
              Assistente LicitarsAI
            </p>
          </div>
        </div>
        {/* Document Selector */}
        <section
          id="document-select-section"
          className="document-select-section"
          aria-labelledby="document-select-label"
          role="region"
          data-testid="document-select-section"
        >
          <h2
            id="document-select-label"
            className="sr-only"
          >
            Seleção e Configuração de Documento
          </h2>
          <div
            id="document-select-wrapper"
            className="document-select-container glass-card border border-white/10 rounded-lg p-3"
            role="group"
            aria-labelledby="document-select-label"
            data-testid="document-select-wrapper"
          >
            <div className="flex items-center gap-3">
              {/* Document Icon */}
              <div className="flex-shrink-0">
                <div className="w-8 h-8 rounded bg-white/10 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-white" />
                </div>
              </div>
              {/* Document Selector */}
              <div className="flex-1 min-w-0">
                <DocumentSelectV3
                  documents={documents}
                  selectedDocumentId={selectedDocumentId}
                  onSelect={onDocumentSelect}
                  disabled={loadingDocuments || loadingSession}
                  showPreview={false}
                />
              </div>
              {/* Loading indicator */}
              {(loadingDocuments || loadingSession) && (
                <div className="flex-shrink-0">
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                </div>
              )}
            </div>
          </div>
          {/* Selected Document Information */}
          {selectedDocument && (
            <div className="mt-3">
              <DocumentInfoModal document={selectedDocument} />
            </div>
          )}
        </section>
      </nav>
    </header>
  );
};

export default Chat8HeaderV3;
